import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { getAuthHeaders } from '../lib/authHeaders';
import {
  X, 
  Send, 
  MessageSquare,
  ChevronLeft,
  Circle,
  Loader2
} from 'lucide-react';

import { API } from '../config/api';

// Helper to get auth headers
// getAuthHeaders imported from shared utility

// Format time
const formatTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diff = now - date;
  
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const ChatModal = ({ isOpen, onClose, recipient, currentUserId }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMessages = useCallback(async () => {
    if (!recipient?.user_id) return;
    
    try {
      const response = await axios.get(
        `${API}/messages/conversation/${recipient.user_id}`,
        { headers: await getAuthHeaders(), withCredentials: true }
      );
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setIsLoading(false);
    }
  }, [recipient?.user_id]);

  useEffect(() => {
    if (isOpen && recipient) {
      fetchMessages();
      // Poll for new messages every 5 seconds
      const interval = setInterval(fetchMessages, 5000);
      return () => clearInterval(interval);
    }
  }, [isOpen, recipient, fetchMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSend = async () => {
    if (!newMessage.trim() || isSending) return;
    
    setIsSending(true);
    try {
      const response = await axios.post(
        `${API}/messages/send`,
        { recipient_id: recipient.user_id, message: newMessage.trim() },
        { headers: await getAuthHeaders(), withCredentials: true }
      );
      
      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
      <div className="bg-slate-900 rounded-2xl w-full max-w-md h-[500px] flex flex-col shadow-2xl border border-slate-700 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#5865F2] to-purple-600 px-4 py-3 flex items-center gap-3">
          <button
            onClick={onClose}
            className="p-1 hover:bg-white/20 rounded-lg transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-white" />
          </button>
          
          {recipient?.avatar ? (
            <img src={recipient.avatar} alt="" className="w-10 h-10 rounded-full border-2 border-white/30" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white font-bold">
              {(recipient?.name || recipient?.username || '?')[0].toUpperCase()}
            </div>
          )}
          
          <div className="flex-1">
            <h3 className="text-white font-semibold">
              {recipient?.name || recipient?.display_name || recipient?.username}
            </h3>
            {recipient?.discord_username && (
              <p className="text-white/70 text-xs">@{recipient.discord_username}</p>
            )}
          </div>
          
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-800/50">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-8 h-8 text-purple-400 animate-spin" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <MessageSquare className="w-12 h-12 text-gray-600 mb-3" />
              <p className="text-gray-400">No messages yet</p>
              <p className="text-gray-500 text-sm">Say hello! 👋</p>
            </div>
          ) : (
            <>
              {messages.map((msg, index) => {
                const isMe = msg.sender_id === currentUserId;
                return (
                  <div
                    key={msg.message_id || index}
                    className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[80%] ${isMe ? 'order-2' : 'order-1'}`}>
                      {!isMe && (
                        <div className="flex items-center gap-2 mb-1">
                          {msg.sender_avatar ? (
                            <img src={msg.sender_avatar} alt="" className="w-5 h-5 rounded-full" />
                          ) : (
                            <div className="w-5 h-5 rounded-full bg-purple-600 flex items-center justify-center text-white text-xs">
                              {(msg.sender_name || '?')[0]}
                            </div>
                          )}
                          <span className="text-xs text-gray-400">{msg.sender_name}</span>
                        </div>
                      )}
                      <div
                        className={`px-4 py-2 rounded-2xl ${
                          isMe
                            ? 'bg-[#5865F2] text-white rounded-br-md'
                            : 'bg-slate-700 text-white rounded-bl-md'
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap break-words">{msg.message}</p>
                      </div>
                      <p className={`text-xs text-gray-500 mt-1 ${isMe ? 'text-right' : 'text-left'}`}>
                        {formatTime(msg.created_at)}
                        {isMe && msg.read && <span className="ml-1">✓</span>}
                      </p>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Input */}
        <div className="p-3 bg-slate-900 border-t border-slate-700">
          <div className="flex gap-2">
            <Input
              ref={inputRef}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type a message..."
              className="flex-1 bg-slate-800 border-slate-600 text-white placeholder-gray-500"
              maxLength={1000}
            />
            <Button
              onClick={handleSend}
              disabled={!newMessage.trim() || isSending}
              className="bg-[#5865F2] hover:bg-[#4752C4] text-white px-4"
            >
              {isSending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatModal;
